import {
  isEditorialArchival,
  isEditorialOnly,
  isFilm,
  isGroupedByEvent,
  isImage,
  SortEnum,
  type SearchParameters,
} from '../searchParameters';
import { isAnchoredSearch } from '../searchMode';
import type { SearchProfile } from '../searchProfile';
import type { SearchResults } from '../searchResults';

export type NLSKeywordInterleaveInputs = Readonly<{
  searchParameters: SearchParameters;
  // A site fact, from site config: whether the site's editorial
  // natural-language results interleave keyword matches at all.
  siteInterleavesKeywords: boolean;
  searchProfile: Pick<SearchProfile, 'hasNLSKeywordInterleaveForMostPopular'>;
  searchResults: Pick<
    SearchResults,
    'count' | 'corrections' | 'enhancedSearch'
  >;
}>;

// Editorial images or film, outside the archive: the results natural-language
// search ranks.
const isNLSRankedContent = (searchParameters: SearchParameters): boolean =>
  isEditorialOnly(searchParameters) &&
  (isImage(searchParameters) || isFilm(searchParameters)) &&
  !isEditorialArchival(searchParameters);

// Event, anchored and asset-id searches all bypass phrase ranking.
const bypassesPhraseRanking = (
  searchParameters: SearchParameters,
  searchResults: NLSKeywordInterleaveInputs['searchResults']
): boolean =>
  isGroupedByEvent(searchParameters) ||
  !!searchParameters.events ||
  isAnchoredSearch(searchParameters) ||
  !!searchResults.corrections?.assetIdCorrected;

// Whether editorial natural-language results interleave keyword matches. The
// fact both display rules start from.
const isNLSKeywordInterleaved = ({
  searchParameters,
  siteInterleavesKeywords,
  searchResults,
}: Pick<
  NLSKeywordInterleaveInputs,
  'searchParameters' | 'siteInterleavesKeywords' | 'searchResults'
>): boolean =>
  siteInterleavesKeywords &&
  isNLSRankedContent(searchParameters) &&
  !bypassesPhraseRanking(searchParameters, searchResults);

// Whether the sort options carry the note that editorial natural-language
// results interleave keyword matches. Shown on zero results too: the note is
// about the sort, not the results. Visitors in the most-popular SAW offer get
// the ribbon on every sort instead, so the note is retired for them.
export const shouldShowNLSSortByMessaging = (
  inputs: Pick<
    NLSKeywordInterleaveInputs,
    | 'searchParameters'
    | 'siteInterleavesKeywords'
    | 'searchProfile'
    | 'searchResults'
  >
): boolean =>
  isNLSKeywordInterleaved(inputs) &&
  !inputs.searchProfile.hasNLSKeywordInterleaveForMostPopular;

// Whether the gallery header shows the ribbon explaining the interleaving.
// Every sort interleaves except most popular, which joins only for visitors in
// the SAW offer. The search must state its sort: read through the schema
// default, an unstated sort would look like best match.
export const shouldShowNLSKeywordInterleaveRibbon = (
  inputs: NLSKeywordInterleaveInputs
): boolean => {
  const { searchParameters, searchProfile, searchResults } = inputs;

  if (
    searchResults.count === 0 ||
    !searchResults.enhancedSearch?.active ||
    !isNLSKeywordInterleaved(inputs) ||
    !searchParameters.states('sort')
  ) {
    return false;
  }

  return (
    searchParameters.sort !== SortEnum.MOST_POPULAR ||
    searchProfile.hasNLSKeywordInterleaveForMostPopular
  );
};
